import {
  ActiveInactiveStatus,
  ApiListResponse,
  SessionPackDTO,
} from "@gymflow/types";
import { AxiosInstance } from "axios";

import { createSortParam } from "../createSortParam";
import { trimFields } from "./features/trimFiels";

export type CreditPackApiType = ReturnType<typeof creditPackApi>;

const creditPackApi = (axiosInstance: AxiosInstance, clubId: number) => {
  const clubPrefix = `club/${clubId}/`;
  const fieldsToTrim = ["name"];

  return {
    find({
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
    }: {
      readonly extraParams?: {
        readonly unpaged?: boolean;
        readonly status?: ActiveInactiveStatus;
        readonly id?: number[];
        readonly hasActivityCategory?: boolean;
        readonly hasAppointmentCategory?: boolean;
        readonly [k: string]: any;
      };
      readonly page?: number;
      readonly limit?: number;
      readonly sort?: {
        readonly field: string;
        readonly desc?: boolean;
      };
    }) {
      const config = {
        params: {
          page,
          size: limit,
          ...extraParams,
        },
      } as any;
      if (sort) {
        config.params.sort = createSortParam(sort);
      }

      return axiosInstance.get<ApiListResponse<SessionPackDTO>>(
        `${clubPrefix}sessionpack`,
        config,
      );
    },
    findById(id: number) {
      return axiosInstance.get<SessionPackDTO>(
        `${clubPrefix}sessionpack/${id}`,
      );
    },
    deactivate(id: number) {
      return axiosInstance.patch(`${clubPrefix}sessionpack/${id}/inactive`);
    },

    activate(id: number) {
      return axiosInstance.patch(`${clubPrefix}sessionpack/${id}/active`);
    },

    create(
      newCreditPack: Partial<SessionPackDTO>,
      termsAndConditionsBlob: any,
    ) {
      const formData = transformIntoFormData(
        {
          file: termsAndConditionsBlob,
          creditPack: { ...newCreditPack, clubIdList: [clubId] },
        },
        fieldsToTrim,
      );

      return axiosInstance.post<SessionPackDTO>(
        `${clubPrefix}sessionpack`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    },

    update(
      id: number,
      {
        patchedFields,
        termsAndConditionsBlob,
      }: {
        patchedFields: Partial<SessionPackDTO>;
        termsAndConditionsBlob: any;
      },
    ) {
      const formData = transformIntoFormData(
        {
          file: termsAndConditionsBlob,
          creditPack: patchedFields,
        },
        fieldsToTrim,
      );

      return axiosInstance.patch(`${clubPrefix}sessionpack/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },

    summary({
      creditPackId,
      promotionCode,
      userMemberId,
    }: {
      creditPackId: number;
      promotionCode?: string;
      userMemberId: string;
    }) {
      return axiosInstance.post(`${clubPrefix}sessionpack/summary`, {
        sessionPackId: creditPackId,
        promotionCode,
        userMemberId,
      });
    },
    delete(sessionPackId: number) {
      return axiosInstance.delete(`${clubPrefix}sessionpack/${sessionPackId}`);
    },
  };
};

export default creditPackApi;

function transformIntoFormData(
  {
    file,
    creditPack,
  }: {
    file: any;
    creditPack: any;
  },
  fieldsToTrim: string[],
) {
  const formData = new FormData();
  const trimmedData = trimFields(creditPack, fieldsToTrim);
  if (file) {
    formData.append("file", file, file.name);
  }

  if (creditPack) {
    formData.append(
      "session_pack",
      new Blob([JSON.stringify(trimmedData)], {
        type: "application/json",
      }),
    );
  }

  return formData;
}
